import { useMemo } from 'react'
import type { CollectableFeeRow } from '@klear/collection'
import { type CollectionStepCtx } from '@/app/collections/outlet-context'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import { COLLECTION_TYPE } from '@/app/collections/steps'
import { Money } from '@klear/money'
import * as changeCase from 'change-case'

function FeeCollectionReviewSummary({
  fees,
  collectableFeeRows,
  totalFormatted
}: {
  fees: { feeId: number; money: Money }[]
  collectableFeeRows: CollectableFeeRow[]
  totalFormatted: string
}) {
  const rowByFeeId = useMemo(() => {
    const m = new Map<number, CollectableFeeRow>()
    for (const r of collectableFeeRows) {
      if (r.fee.id != null) m.set(r.fee.id, r)
    }
    return m
  }, [collectableFeeRows])

  return (
    <>
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">Fees</dt>
        <dd>{fees.length} selected</dd>
      </div>
      {fees.map((f) => {
        const row = rowByFeeId.get(f.feeId)
        const typeLabel = changeCase.capitalCase(row?.fee.type ?? 'fee')
        const offerLabel = row?.invoiceOffer?.idDisplay ?? (row?.fee.parentId != null ? `#${row.fee.parentId}` : '')
        const isPartial = row ? f.money.lessThan(row.remainingAmount) : false
        return (
          <div key={f.feeId} className="flex justify-between gap-4 pl-2">
            <dt className="text-muted-foreground">
              {typeLabel}
              {offerLabel !== '' && <span className="text-xs text-muted-foreground"> — offer {offerLabel}</span>}
              {isPartial && <span className="ml-1 text-xs">(partial)</span>}
            </dt>
            <dd>{f.money.toString()}</dd>
          </div>
        )
      })}
      <div className="flex justify-between gap-4 font-medium border-t pt-2 mt-2">
        <dt>Total</dt>
        <dd>{totalFormatted}</dd>
      </div>
    </>
  )
}

function AdvanceRecoveryReviewSummary({
  recoveries,
  collectableRecoveries,
  totalFormatted
}: {
  recoveries: { orderEventId: number; money: Money }[]
  collectableRecoveries: {
    id: number
    idDisplay?: string
    remainingAmount: Money
  }[]
  totalFormatted: string
}) {
  const recoveryById = useMemo(() => new Map(collectableRecoveries.map((r) => [r.id, r])), [collectableRecoveries])
  return (
    <>
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">Recoveries</dt>
        <dd>{recoveries.length} selected</dd>
      </div>
      {recoveries.map((r) => {
        const detail = recoveryById.get(r.orderEventId)
        const label = detail?.idDisplay ?? `#${r.orderEventId}`
        const isPartial = detail ? r.money.lessThan(detail.remainingAmount) : false
        return (
          <div key={r.orderEventId} className="flex justify-between gap-4 pl-2">
            <dt className="text-muted-foreground">
              Offer {label}
              {isPartial && <span className="ml-1 text-xs">(partial)</span>}
            </dt>
            <dd>{r.money.toString()}</dd>
          </div>
        )
      })}
      <div className="flex justify-between gap-4 font-medium border-t pt-2 mt-2">
        <dt>Total</dt>
        <dd>{totalFormatted}</dd>
      </div>
    </>
  )
}

export default function ReviewStep({ ctx }: WizardStepComponentProps<CollectionStepCtx>) {
  const { state, collectableFeeRows, collectableRecoveries } = ctx
  const isFeeCollection = state.collectionType === COLLECTION_TYPE.FEES
  const isAdvanceRecovery = state.collectionType === COLLECTION_TYPE.ADVANCE_RECOVERY

  const feeTotal = useMemo(() => Money.sum(state.selectedFees.map((f) => f.money)), [state.selectedFees])
  const recoveryTotal = useMemo(
    () => Money.sum(state.selectedRecoveries.map((r) => r.money)),
    [state.selectedRecoveries]
  )

  return (
    <div className="space-y-4 w-full">
      <p className="text-sm text-muted-foreground">Review and collect.</p>
      <dl className="space-y-1 text-sm">
        {isFeeCollection && (
          <FeeCollectionReviewSummary
            fees={state.selectedFees}
            collectableFeeRows={collectableFeeRows}
            totalFormatted={feeTotal.toString()}
          />
        )}
        {isAdvanceRecovery && (
          <AdvanceRecoveryReviewSummary
            recoveries={state.selectedRecoveries}
            collectableRecoveries={collectableRecoveries}
            totalFormatted={recoveryTotal.toString()}
          />
        )}
      </dl>
    </div>
  )
}
